import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink, Lock } from "lucide-react";
import { toast } from "sonner";
import { http, formatError } from "@/lib/api";
import { PLAN_NAMES, useLoad, useStaff } from "@/lib/staff";
import { BTN_GHOST, Badge, ErrorBox, RoleBadge, Skeleton, StatusBadge } from "@/components/staff/ui";
import { ActivityCard, AuditCard, ChatsCard, DevicesCard, ErrorsCard, PaymentsCard, PlanCard, PlanHistoryCard, ProfileCard, UsageCard } from "@/components/staff/StudentCards";
import { StudentActionsPanel } from "@/components/staff/StudentActionsPanel";
import { VerifyStatus } from "@/components/staff/VerifyIdentity";

function StripeButton({ uid }) {
  const open = async () => {
    const w = window.open("about:blank", "_blank");
    try {
      const { data } = await http.get(`/staff/stripe-link/${uid}`);
      if (w) { w.opener = null; w.location.href = data.url; } else window.location.href = data.url;
    } catch (e) {
      w?.close();
      toast.error(formatError(e));
    }
  };
  return <button onClick={open} className={BTN_GHOST} data-testid="student-stripe-link"><ExternalLink className="h-4 w-4" />Open in Stripe</button>;
}

function Header({ d }) {
  const { can } = useStaff();
  const p = d.profile;
  return (
    <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
      <div className="min-w-0">
        <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-white truncate" data-testid="student-name">{p.name || p.email}</h1>
        <p className="text-sm text-neutral-400 truncate" data-testid="student-email">{p.email}</p>
        <div className="flex flex-wrap items-center gap-1.5 mt-2">
          <StatusBadge status={p.status} testid="student-status" />
          <Badge tone="neutral" testid="student-plan-badge">{PLAN_NAMES[d.plan.tier] || d.plan.tier}</Badge>
          <RoleBadge role={p.staff_role} testid="student-staff-role" />
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {can("students.delete") && d.can_act && <VerifyStatus />}
        {can("billing.view") && p.has_stripe_customer && <StripeButton uid={p.user_id} />}
      </div>
    </div>
  );
}

const PrivacyNote = () => (
  <p className="flex items-center gap-2 text-xs text-neutral-400 rounded-xl border border-[#1B2740] bg-[#0C1322] px-4 py-2.5 mb-5" data-testid="student-privacy-note">
    <Lock className="h-3.5 w-3.5 text-sky-300 shrink-0" />Chat messages, uploaded files and AI answers are private to the student and are never shown to staff.
  </p>
);

export default function StudentDetailPage() {
  const { uid } = useParams();
  const { data: d, error, loading, reload } = useLoad(`/staff/students/${uid}`);
  return (
    <div data-testid="staff-student-detail">
      <Link to="/staff/students" className="inline-flex items-center gap-1.5 text-sm text-neutral-400 hover:text-white mb-4" data-testid="student-back-link">
        <ArrowLeft className="h-4 w-4" />Students
      </Link>
      {error && <div className="mb-4"><ErrorBox error={error} onRetry={reload} /></div>}
      {!d && loading && <Skeleton rows={8} />}
      {d && (
        <>
          <Header d={d} />
          <PrivacyNote />
          <div className="grid xl:grid-cols-3 gap-4 items-start">
            <div className="xl:col-span-2 space-y-4">
              <ProfileCard p={d.profile} />
              <PlanCard d={d} onChanged={reload} />
              <PaymentsCard rows={d.payments} />
              <UsageCard u={d.usage_30d} />
              <ChatsCard chats={d.chats} />
              <ActivityCard rows={d.recent_ai} />
            </div>
            <div className="space-y-4">
              <StudentActionsPanel d={d} onDone={reload} />
              <PlanHistoryCard rows={d.plan_history} />
              <DevicesCard devices={d.devices} sessions={d.open_sessions} />
              <ErrorsCard uid={uid} rows={d.errors} />
              <AuditCard uid={uid} rows={d.audit} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
